import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { BuilderPublicShowroomCTACustomFieldConfig } from "api-types/builders/salesConfig";
import type { ClientCustomer, ClientCustomerInfoFields } from "api-types/customers/customersResponses";
import type { ClientHomePlan } from "api-types/homePlans/homePlanResponses";
import { ensureDefined } from "common/utils/assertOfType";
import { toTitleCase } from "common/utils/strings";
import { CustomField } from "lib/components/builders/communities/plans/showroom/CustomFields";
import { getBuilderURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import Loading from "ui/components/Loading";
import Modal from "ui/components/Modal";
import InputGroupText from "ui/forms/InputGroupText";
import useAppContext from "ui/hooks/useAppContext";
import { Message } from "ui/text/Message";
import { useCustomer, useUpdateHomePlan } from "./useHomePlanAPI";

interface CustomerInfoModalProps {
  homePlanId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  customerId?: string | null;
  onUpdate?: (homePlan: ClientHomePlan) => void;
}

export default function CustomerInfoModal({ homePlanId, customerId, open, setOpen, onUpdate }: CustomerInfoModalProps) {
  const { builderId } = useAppRouterParams();
  const { setUserNotification } = useAppContext();
  const { hasPermission } = useUserContext();
  const updateHomePlan = useUpdateHomePlan(builderId ?? "");
  const { data: customer, loading } = useCustomer({ builderId, customerId });
  const { data: builder } = useFetch<ClientBuilder>(getBuilderURL(ensureDefined(builderId)), [builderId]);

  const [submitting, setSubmitting] = useState(false);

  const [customerInfoFields, setCustomerInfoFields] = useState<ClientCustomerInfoFields>({
    externalId: customer?.externalId,
    name: customer?.name,
    email: customer?.email,
    phone: customer?.phone,
  });

  const [customerCustomFields, setCustomerCustomFields] = useState<ClientCustomer["customFields"]>(
    customer?.customFields ?? {},
  );

  useEffect(() => {
    if (customer) {
      setCustomerInfoFields({
        externalId: customer?.externalId ?? undefined,
        name: customer?.name,
        email: customer?.email,
        phone: customer?.phone,
      });
      setCustomerCustomFields(customer?.customFields ?? {});
    }
  }, [customerId, customer]);

  const builderCustomFields = builder?.salesConfig?.publicShowroomCTA?.customFields ?? [];
  const allCustomFieldKeys = [
    ...new Set(builderCustomFields.map((f) => f.name).concat(Object.keys(customerCustomFields ?? {}))),
  ];

  const getCustomFieldConfig = (fieldName: string) => {
    return (
      (builderCustomFields.find((f) => f.name === fieldName) as BuilderPublicShowroomCTACustomFieldConfig) || {
        type: "TEXT",
        name: fieldName,
        required: false,
      }
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const homePlan = await updateHomePlan.mutateAsync({
        homePlanId,
        fields: {
          customerInfo: customerInfoFields,
          customerCustomFields,
        },
      });
      setUserNotification({ message: "Customer information updated.", messageType: "success" });
      onUpdate?.(homePlan);
      setOpen(false);
    } catch {
      setUserNotification({ message: "Failed to update customer information.", messageType: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      size="small"
      title="Customer Info"
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          {" "}
          <Button btnSize="large" btnStyle="tertiary" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            btnSize="large"
            btnStyle="primary"
            onClick={handleSubmit}
            isPending={submitting}
            disabled={!hasPermission("EDIT_CUSTOMER_INFO")}
          >
            Save
          </Button>
        </>
      }
    >
      <Message marginBottom>View and edit customer information.</Message>
      {customerId && loading ? (
        <Loading />
      ) : (
        <>
          {hasPermission("VIEW_INTERNAL_IDS") && (
            <div key="id" style={{ marginBottom: 10 }}>
              <InputGroupText
                type="text"
                labelText="ID"
                inputValue={customer?.id}
                placeholder="No Customer record created yet."
                tooltip="This is the internal ID of the customer record in Higharc. Only Super Admins can see this."
                disabled
              />
            </div>
          )}
          {Object.keys(customerInfoFields).map((keyName) => (
            <div key={keyName} style={{ marginBottom: 10 }}>
              <InputGroupText
                type="text"
                labelText={keyName === "externalId" ? "External ID" : toTitleCase(keyName)}
                inputValue={customerInfoFields[keyName]}
                onChange={(s: string) => setCustomerInfoFields({ ...customerInfoFields, [keyName]: s })}
                placeholder="Enter value"
                disabled={!hasPermission("EDIT_CUSTOMER_INFO")}
              />
            </div>
          ))}
          {allCustomFieldKeys.map((keyName, index) => (
            <div key={keyName} style={{ marginBottom: 10 }}>
              <CustomField
                key={keyName}
                field={getCustomFieldConfig(keyName)}
                index={index}
                value={customerCustomFields?.[keyName] ?? ""}
                onChange={(value: string) => setCustomerCustomFields({ ...customerCustomFields, [keyName]: value })}
                disabled={!hasPermission("EDIT_CUSTOMER_INFO")}
              />
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}
